import { useLatest } from 'ahooks';
import { cloneDeep, pick } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import IconFont from '@meitu/xdesign-icon-font';
import {
    BaseLayerView,
    BaseStagePlugin,
    checkPointInStage,
    convertToLayerPoint,
    StageScaleView,
    useFramework,
    useIsBasicOperate,
    useMoveEventListener,
    useScale,
} from '@/framework';
import { getEditorState } from '@/framework/core/states/editor-state';
import { getCellStyle } from '@/layers/table-layer/utils/table';
import { getCellInfoListByMousePoint } from '@/layers/table-layer/view/cell-component';
import { registerStyleSheet } from '@/utils/common-kit';
import { isHotKeyPress } from '@/utils/event-kit';
import { IExportPlugin, IPoint, LayerType, PluginType } from '@/version-types';
import { registerTableContextMenu } from './components/context-menu';
import { registerSelectedRowOrColShortcut } from './components/selected-row-or-col-shortcut';
import TableCellSelectedRect, { tableCellSelectedRectId } from './components/table-cell-selected-rect';
import TableController from './components/table-controller';
import { TABLE_CONTROLLER_EVENTS } from './events';
import { useActiveTableLayer, useTableEditNodeCursorCss, useTextBizHotKey } from './hooks';
import tableEditPluginState, { CellsSelectedInfo, useTableEditState } from './state';
import { TableAction } from './table-action';
import { TableEdit } from './table-edit';
import { appendCellsSelectedInfoByPoint, getCellsSelectedInfoByMouseRange } from './utils/cell';
import { isOnlyClick } from './utils/native';

const TableEditWidget = React.memo(() => {
    // --- framework states ---
    const framework = useFramework();
    const scale = useScale();

    const isBasicOperate = useIsBasicOperate();

    // --- framework actions ---
    const { getState } = useTableEditState();
    const clearTableEditState = useTableEditState().getState().initState;

    // --- internal states ---
    const { setState } = useTableEditState();
    const activeCell = useTableEditState()((s) => s.activeCell);
    const hasEditingText = useTableEditState()((s) => s.hasEditingText);
    const hasSelectedInfo = useTableEditState()((s) => !!s.cellsSelectedInfo || !!s.rowOrColSelectedRectInfo);
    const rowOrColSelectedRectInfo = useTableEditState()((s) => s.rowOrColSelectedRectInfo);
    const activeTableLayer = useActiveTableLayer();
    const isActiveTableLayer = activeTableLayer !== null;
    const [tableEditNode, setTableEditNode] = useState<HTMLElement | null>(null);
    const [isMouseWheelSetRowOrColCount, setIsMouseWheelSetRowOrColCount] = useState(false);

    useEffect(() => {
        const startMouseWheelSetRowOrColCountListenerID = framework.events.on(
            TABLE_CONTROLLER_EVENTS.START_MOUSE_WHEEL_SET_ROW_OR_COL_COUNT,
            () => {
                setIsMouseWheelSetRowOrColCount(true);
            },
        );
        const endMouseWheelSetRowOrColCountListenerID = framework.events.on(
            TABLE_CONTROLLER_EVENTS.END_MOUSE_WHEEL_SET_ROW_OR_COL_COUNT,
            () => {
                setIsMouseWheelSetRowOrColCount(false);
            },
        );
        return () => {
            framework.events.off(startMouseWheelSetRowOrColCountListenerID);
            framework.events.off(endMouseWheelSetRowOrColCountListenerID);
        };
    }, []);

    useTableEditNodeCursorCss({
        tableEditNode,
        isActiveTableLayer,
        isDisableMoveState: hasSelectedInfo,
    });

    // --- internal refs ---
    const activeTableLayerRef = useLatest(activeTableLayer);

    useTextBizHotKey();

    useEffect(() => {
        if (activeTableLayer?.id) {
            const removeStyleSheet = registerStyleSheet(`
                .active-operate {
                    border-color: transparent;
                }
            `);
            return () => {
                clearTableEditState();
                removeStyleSheet();
            };
        }
    }, [activeTableLayer?.id]);

    useEffect(() => {
        if (!isActiveTableLayer) return;
        if (!hasSelectedInfo) return;
        framework.operate.disableMove();
        return () => framework.operate.enableMove();
    }, [isActiveTableLayer, hasSelectedInfo]);

    useEffect(() => {
        const baseOperateListenerID = framework.events.onBaseOperate((layerId) => {
            if (framework.getLayer(layerId).layerType !== LayerType.TABLE) return;

            setState({
                hasEditingText: false,
                baseCellsBeforeEditing: null,
            });
        });
        // 撤销or重做时, 取消单元格选择态 (issue: https://jira.meitu.com/browse/MTXXPRO-30744)
        const historyListenerID = framework.history.onBeforeChange(({ isRedo, isUndo }) => {
            if (!activeTableLayerRef.current) return;
            if (isRedo || isUndo) {
                clearTableEditState();
            }
        });
        return () => {
            framework.events.off(baseOperateListenerID);
            framework.events.off(historyListenerID);
        };
    }, []);

    // 记录表格移动开始时的位置：否则，选中单元格的情况下，移动表格会导致选中位置发生改变
    const moveStartLayerInfo = useRef<{
        top: number;
        left: number;
        width: number;
        height: number;
    } | null>(null);

    // 单元格多选/单选 等逻辑
    useMoveEventListener(({ startPoint, lastPoint, isStart, isEnd }) => {
        const activeTableLayer = activeTableLayerRef.current;
        if (!activeTableLayer) return;

        const activeStageIndex = framework.stage.getActiveStageIndex();
        if (!checkPointInStage(framework.identity, startPoint, activeStageIndex)) return;

        const { cellsSelectedInfo, rowOrColSelectedRectInfo, activeCell } = getState();
        const tableCanSelected = !!activeCell || !!cellsSelectedInfo || !!rowOrColSelectedRectInfo;
        if (!tableCanSelected) return;
        if (isStart) {
            moveStartLayerInfo.current = {
                top: activeTableLayer.top,
                left: activeTableLayer.left,
                width: activeTableLayer.width,
                height: activeTableLayer.height,
            };
            return;
        }

        /** 画布左上角开始的坐标系 */
        const startCoords = convertToLayerPoint(framework.identity, startPoint, activeStageIndex);
        /** 画布左上角开始的坐标系 */
        const lastCoords = convertToLayerPoint(framework.identity, lastPoint, activeStageIndex);
        const checkCoordsIsInTableLayer = (coords: IPoint) => {
            const layerInfo = moveStartLayerInfo.current ?? activeTableLayer;
            return (
                coords.x >= layerInfo.left &&
                coords.x <= layerInfo.left + layerInfo.width &&
                coords.y >= layerInfo.top &&
                coords.y <= layerInfo.top + layerInfo.height
            );
        };
        if (!checkCoordsIsInTableLayer(startCoords)) return;

        const cellsIsSelecting = !isStart && !isEnd;

        const newCellsSelectedInfo = getCellsSelectedInfoByMouseRange({
            tableLayer: activeTableLayer,
            range: {
                startCoords: {
                    stageX: startCoords.x,
                    stageY: startCoords.y,
                },
                lastCoords: {
                    stageX: lastCoords.x,
                    stageY: lastCoords.y,
                },
            },
        });
        const newActiveCell = newCellsSelectedInfo && newCellsSelectedInfo.cellInfoList.length > 1 ? null : activeCell;
        setState({
            activeCell: newActiveCell,
            cellsIsSelecting,
            cellsSelectedInfo: newCellsSelectedInfo,
            rowOrColSelectedRectInfo: null,
        });
    });

    // 取消编辑态
    useEffect(() => {
        if (activeCell === null) {
            setState({ hasEditingText: false, baseCellsBeforeEditing: null });
        }
    }, [activeCell]);

    // 同步到text-edit-plugin的编辑态
    useEffect(() => {
        const layerId = activeTableLayerRef.current?.id;
        if (!layerId) return;

        if (hasEditingText) {
            framework.getInstance(TableEdit).enterEditing(layerId);
        } else if (framework.getInstance(TableEdit).getEditingLayer()) {
            // 不处于编辑态时, 不能调用exitEditing, 切换单元格时才调用
            framework.getInstance(TableEdit).exitEditing();
        }
    }, [hasEditingText]);

    // 菜单
    useEffect(() => {
        if (!hasSelectedInfo) return;
        const layerId = activeTableLayerRef.current?.id;
        if (!layerId) return;
        const unregister = registerTableContextMenu(framework.identity, layerId);
        return unregister;
    }, [hasSelectedInfo]);

    useEffect(() => {
        if (!activeTableLayer?.id) return;
        if (!rowOrColSelectedRectInfo) return;
        const layerDom = tableEditNode?.querySelector<HTMLElement>('.layer-view__content');
        if (!layerDom) return;
        const unregister = registerSelectedRowOrColShortcut({
            layerDom,
            frameworkIdentity: framework.identity,
            layerId: activeTableLayer.id,
            rowOrColSelectedRectInfo,
        });
        return unregister;
    }, [tableEditNode, framework.identity, rowOrColSelectedRectInfo, activeTableLayer?.id]);

    // 如果单元格处于选中态, 则拦截shift多选图层的逻辑, 保证能走到click的shift多选逻辑
    useEffect(() => {
        const mouseDownListenerID = framework.events.onMouseDown(({ clientX, clientY }) => {
            const activeTableLayer = activeTableLayerRef.current;
            if (!activeTableLayer) return;
            const isShowContextMenu = getEditorState(framework.identity).showContextMenu;
            // 有contextMenu时, 这下点击是关闭contextMenu，所以不做任何处理
            if (isShowContextMenu) return;

            const targetCellInfoList = getCellInfoListByMousePoint({ clientX, clientY }, activeTableLayer.id);

            const targetCellInfo = targetCellInfoList[0];
            if (!targetCellInfo) return;
            const targetCell = activeTableLayer.data.cells[targetCellInfo.vIndex]?.[targetCellInfo.hIndex];
            if (!targetCell) return;

            const { cellsSelectedInfo } = getState();
            const hasSelectedRange = !!cellsSelectedInfo?.cellInfoList.length;
            // shift多选逻辑
            if (isHotKeyPress() && hasSelectedRange) return true;
        }, 9);
        return () => framework.events.off(mouseDownListenerID);
    }, []);

    useEffect(() => {
        if (activeTableLayer?.lock) {
            clearTableEditState();
        }
    }, [activeTableLayer?.lock]);

    if (activeTableLayer === null) return null;
    if (activeTableLayer?.lock) return null;

    const handleClick = ({ clientX, clientY }: React.MouseEvent<HTMLDivElement>) => {
        const activeTableLayer = activeTableLayerRef.current;
        if (!activeTableLayer) return;
        const isShowContextMenu = getEditorState(framework.identity).showContextMenu;
        // 有contextMenu时, 这下点击是关闭contextMenu，所以不做任何处理
        if (isShowContextMenu) return;
        if (!isOnlyClick({ clientX, clientY })) return;

        const targetCellInfoList = getCellInfoListByMousePoint({ clientX, clientY }, activeTableLayer.id);

        const targetCellInfo = targetCellInfoList[0];
        if (!targetCellInfo) return;
        const targetCell = activeTableLayer.data.cells[targetCellInfo.vIndex]?.[targetCellInfo.hIndex];
        if (!targetCell) return;

        const { activeCell, cellsSelectedInfo } = getState();
        const hasSelectedRange = !!cellsSelectedInfo?.cellInfoList.length;
        // shift多选逻辑
        if (isHotKeyPress() && hasSelectedRange) {
            const _selectedRect = document.getElementById(tableCellSelectedRectId)?.getBoundingClientRect();
            if (!_selectedRect) return;
            const activeStageIndex = framework.stage.getActiveStageIndex();
            if (!checkPointInStage(framework.identity, { x: clientX, y: clientY }, activeStageIndex)) return;
            /** 画布左上角开始的坐标系 */
            const selectedRect = {
                topLeft: convertToLayerPoint(
                    framework.identity,
                    { x: _selectedRect.left, y: _selectedRect.top },
                    activeStageIndex,
                ),
                rightBottom: convertToLayerPoint(
                    framework.identity,
                    { x: _selectedRect.right, y: _selectedRect.bottom },
                    activeStageIndex,
                ),
            };
            /** 画布左上角开始的坐标系 */
            const point = convertToLayerPoint(framework.identity, { x: clientX, y: clientY }, activeStageIndex);
            const newCellsSelectedInfo = appendCellsSelectedInfoByPoint({
                tableLayer: activeTableLayer,
                originalSelectedRect: selectedRect,
                point,
            });
            const newActiveCell =
                newCellsSelectedInfo && newCellsSelectedInfo.cellInfoList.length > 1 ? null : activeCell;
            setState({
                activeCell: newActiveCell,
                cellsSelectedInfo: newCellsSelectedInfo,
                rowOrColSelectedRectInfo: null,
            });
            return;
        }
        if (targetCell.__renderId === activeCell?.id) {
            setState({
                hasEditingText: true,
                baseCellsBeforeEditing: cloneDeep(activeTableLayer.data.baseCells),
            });
            return;
        }

        // 选中其他单元格时，取消当前单元格的编辑态
        if (getState().hasEditingText) {
            setState({
                hasEditingText: false,
                baseCellsBeforeEditing: null,
            });
        }

        const newCellsSelectedInfo: CellsSelectedInfo = {
            rect: {
                startHIndex: targetCell.hIndex,
                endHIndex: targetCell.hIndex + targetCell.colSpan,
                startVIndex: targetCell.vIndex,
                endVIndex: targetCell.vIndex + targetCell.rowSpan,
            },
            cellInfoList: [targetCellInfo],
        };

        setState({
            activeCell: {
                id: targetCell.__renderId,
                hIndex: targetCell.hIndex,
                vIndex: targetCell.vIndex,
            },
            cellsIsSelecting: false,
            cellsSelectedInfo: newCellsSelectedInfo,
            rowOrColSelectedRectInfo: null,
        });
    };

    const getTableController = () => {
        const { baseCells, cells: cellsProp } = activeTableLayer.data;
        const firstCellStyle = getCellStyle(cellsProp[0][0]!, activeTableLayer.data);
        const tableFakeBorderTop = (firstCellStyle.border?.top?.width ?? 0) / 2;
        const tableFakeBorderLeft = (firstCellStyle.border?.left?.width ?? 0) / 2;
        return (
            <>
                {!isBasicOperate && !isMouseWheelSetRowOrColCount && (
                    <>
                        <TableController
                            tableInfo={pick(activeTableLayer, ['id', 'width', 'height', 'top', 'left', 'rotate'])}
                            tableLayerData={activeTableLayer.data}
                        />
                        <TableCellSelectedRect
                            baseCells={baseCells}
                            style={{
                                transform: `translate(${tableFakeBorderLeft}px, ${tableFakeBorderTop}px)`,
                            }}
                        />
                    </>
                )}
                <div
                    className="tableLayerMoveArea"
                    // 加这个才可移动layer
                    data-layer-id={activeTableLayer.id}
                    onMouseDownCapture={() => framework.operate.enableMove()}
                >
                    <IconFont name="ArrowFourDirectional" fontSize={18} />
                </div>
            </>
        );
    };

    return (
        <>
            <BaseStagePlugin originIndexOrLayerId={activeTableLayer.id} name="table-edit">
                <StageScaleView originIndex={framework.tryGetLayerOriginIndex(activeTableLayer.id) || 0}>
                    <div style={{ '--stage-scale': scale } as React.CSSProperties} onClick={handleClick}>
                        <BaseLayerView ref={setTableEditNode} disableReactiveArea layer={activeTableLayer}>
                            {getTableController()}
                        </BaseLayerView>
                    </div>
                </StageScaleView>
            </BaseStagePlugin>
        </>
    );
});

const EmitCustomEvents = React.memo(() => {
    const framework = useFramework();

    const hasEditingText = useTableEditState()((s) => s.hasEditingText);
    const selectedInfo = useTableEditState()((s) => ({
        cellsSelectedInfo: s.cellsSelectedInfo,
        rowOrColSelectedRectInfo: s.rowOrColSelectedRectInfo,
    }));
    const oldSelectedInfoRef = useRef(selectedInfo);

    useEffect(() => {
        framework.events.emit(TABLE_CONTROLLER_EVENTS.TABLE_EDIT_STATE_CHANGE, hasEditingText);
    }, [hasEditingText]);

    useEffect(() => {
        const isEqual = (a: typeof selectedInfo, b: typeof selectedInfo) => JSON.stringify(a) === JSON.stringify(b);
        if (!isEqual(oldSelectedInfoRef.current, selectedInfo)) {
            framework.events.emit(TABLE_CONTROLLER_EVENTS.SELECTED_INFO_UPDATE, selectedInfo);
            oldSelectedInfoRef.current = selectedInfo;
        }
    }, [selectedInfo]);

    const lineIsDraggingState = useTableEditState()((s) => s.lineIsDragging);
    const oldLineIsDraggingState = useRef(lineIsDraggingState);

    useEffect(() => {
        if (oldLineIsDraggingState.current !== lineIsDraggingState) {
            framework.events.emit(TABLE_CONTROLLER_EVENTS.LINE_IS_DRAGGING_STATE_UPDATE, lineIsDraggingState);
            oldLineIsDraggingState.current = lineIsDraggingState;
        }
    }, [lineIsDraggingState]);

    return null;
});

const exportPlugin: IExportPlugin = {
    type: PluginType.STAGE,
    sort: 3,
    name: 'table-edit',
    render: () => (
        <>
            <TableEditWidget />
            <EmitCustomEvents />
        </>
    ),
    createState() {
        return [tableEditPluginState];
    },
    createInstance({ identity }) {
        return [
            { key: 'TableEdit', value: new TableEdit(identity) },
            { key: 'TableAction', value: new TableAction(identity) },
        ];
    },
};

export default exportPlugin;
